import { useQuery } from "@blitzjs/rpc"
import { SButton, SFlex } from "@superwall-me/sui"
import ModalOverlay from "app/core/components/layout/ModalOverlay"
import useGetPaywallTemplates from "app/core/data/paywall/useGetPaywallTemplates"
import useIsAdmin from "app/core/hooks/useIsAdmin"
import { trpc } from "app/core/trpcClient"
import { CloneOptionType } from "app/paywalls/validations"
import { HoverSkew } from "app/splash/components/HoverSkew"
import _ from "lodash"
import moment from "moment"
import { queryTypes, useQueryState } from "next-usequerystate"
import { useRouter } from "next/router"
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react"
import tw from "tailwind-styled-components"
import getPaywallPublic from "../queries/getPaywallPublic"
import { TPaywallTemplates } from "../queries/getPaywallTemplates"
import DuplicatePaywallAction from "./actionOverlays/DuplicatePaywallAction"
import PaywallPreview from "./PaywallPreview"
import TemplateSkeleton from "./tables/TemplateSkeleton"

const TemplateNameGroup = tw.div`
  relative
  group
  mb-0
`

const Name = tw.div`
  max-w-[100%]
  overflow-hidden
  text-accent-light
  text-center
  text-ellipsis
  whitespace-nowrap
  text-md
`

const NameTooltip = tw.div<{ $allowTooltip: boolean }>`
  absolute
  top-[26px]
  left-[50%]
  translate-x-[-50%]
  z-10
  opacity-0
  invisible
  shadow-lg
  rounded-lg
  py-1
  px-3
  bg-grayscale-medium
  text-sm
  text-white
  text-opacity-60

  transition
  ease-in-out
  delay-150
  duration-300

  ${(p) => (p.$allowTooltip ? `group-hover:opacity-100 group-hover:visible` : "")}

`

const TemplateName = ({ name }: { name?: string }) => {
  const nameRef = useRef() as RefObject<HTMLDivElement | null>

  const { offsetWidth, scrollWidth } = nameRef?.current || {
    offsetWidth: undefined,
    scrollWidth: undefined,
  }

  const showTooltip = useMemo(() => {
    return offsetWidth !== undefined && scrollWidth !== undefined
      ? offsetWidth < scrollWidth
      : false
  }, [offsetWidth, scrollWidth])

  return (
    <TemplateNameGroup>
      <Name ref={nameRef as RefObject<HTMLDivElement>}>{name}</Name>
      <NameTooltip $allowTooltip={showTooltip}>{name}</NameTooltip>
    </TemplateNameGroup>
  )
}

const PreviewImage = ({ url, placeholder }: { url?: string; placeholder?: string }) => {
  const [isError, setIsError] = useState(false)
  const checkImage = useCallback(() => {
    if (!url) return
    const image = new Image()
    image.src = url || ""

    image.onload = () => setIsError(false)
    image.onerror = () => setIsError(true)
  }, [url])

  useEffect(() => {
    checkImage()
  }, [checkImage])

  if (isError) {
    return (
      <div className="border border-white border-opacity-10 p-1.5 rounded-2xl shadow-2xl bg-grayscale-lightest/10 h-full w-full">
        <div className="h-full w-full flex items-center justify-center bg-grayscale-darker rounded-[12px] text-grayscale-lightest">
          Error loading image
        </div>
      </div>
    )
  }

  return (
    <div className="border border-white border-opacity-10 p-1.5 rounded-2xl shadow-2xl bg-grayscale-lightest/10 backdrop-blur-xl h-full w-full">
      {placeholder ? (
        <div className="h-full aspect-[9/19] w-full flex items-center justify-center bg-grayscale-darker rounded-[12px] text-grayscale-lightest">
          {placeholder}
        </div>
      ) : (
        <img className="rounded-[12px]" src={url} alt="Preview" />
      )}
    </div>
  )
}

const Container = tw.div`
  flex flex-col items-center justify-center
  py-5

`

const NoTemplateMessage = tw.div`
  my-8
  text-lg
  text-grayscale-lightest
`

const TemplatePreviewContainer = tw.div`
  flex
  flex-col
  justify-start
  items-center
  transition-all
  ease-out
  h-full
  hover:border-border-lighter
  duration-[0.2s]
`

const Sublabel = tw.div`
  pb-4
  opacity-40
  text-xs
  text-white
  tracking-loose
`

const ImageContainer = tw.div`
  ImageContainer
  relative
  w-full
  h-full
`

export type TTemplateSortBy = "createdAtAsc" | "createdAtDesc" | "updatedAtAsc" | "updatedAtDesc"
type TTemplate = TPaywallTemplates["paywallTemplates"][number]

const TemplateGallery = ({
  applicationId,
  templateSortBy,
  v4Only,
  isPublic = false,
}: {
  applicationId?: number
  v4Only: boolean
  templateSortBy: TTemplateSortBy
  isPublic?: boolean
}) => {
  const router = useRouter()
  const [initialTemplateId] = useQueryState("templateId", queryTypes.integer)

  const isAdmin = useIsAdmin()

  const {
    data,
    isLoading: isLoadingTemplates,
    isFetchingNextPage: isFetchingNextTemplates,
  } = useGetPaywallTemplates(v4Only, isPublic)

  // if initialTemplateId is NaN, try and fetch an unlisted template with the same id
  const { data: unlistedTemplate } = trpc.paywalls.getUnlistedTemplateById.useQuery(
    { templateId: router.query.templateId as string },
    { enabled: isNaN(Number(initialTemplateId)) && !!router.query.templateId }
  )

  const { paywallTemplates: templates } = data || {}

  const sortedTemplates = useMemo(() => {
    const sortedRegularTemplates = (templates || []).sort((a, b) => {
      if (templateSortBy === "createdAtDesc") {
        return b.createdAt.getTime() - a.createdAt.getTime()
      }
      if (templateSortBy === "createdAtAsc") {
        return a.createdAt.getTime() - b.createdAt.getTime()
      }
      if (templateSortBy === "updatedAtAsc") {
        return a.updatedAt.getTime() - b.updatedAt.getTime()
      }
      if (templateSortBy === "updatedAtDesc") {
        return b.updatedAt.getTime() - a.updatedAt.getTime()
      }

      // Default should be "updatedAtDesc"
      return b.updatedAt.getTime() - a.updatedAt.getTime()
    })

    // Append unlisted template if it exists (we do this to prevent the user from seeing a gap in the grid)
    return unlistedTemplate ? [...sortedRegularTemplates, unlistedTemplate] : sortedRegularTemplates
  }, [templates, templateSortBy, unlistedTemplate])

  const templatePreview = (template: TTemplate) => {
    if (!template) return

    const { id } = template
    const allPreviews = templates.flatMap((template) => template.previews)

    const preview = _.find(allPreviews, (preview) => preview?.paywallId === id && preview?.active)

    return preview
  }

  if (isLoadingTemplates) {
    return (
      <Container>
        <div className="max-w-7xl grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-8 lg:gap-x-5 md:gap-y-10 lg:gap-y-12">
          <TemplateSkeleton />
          <TemplateSkeleton />
          <TemplateSkeleton />
          <TemplateSkeleton />
        </div>
      </Container>
    )
  }

  if (sortedTemplates.length === 0) {
    return (
      <Container>
        <SFlex justify="center" align="center">
          <NoTemplateMessage>No templates found</NoTemplateMessage>
        </SFlex>
      </Container>
    )
  }

  return (
    <Container>
      <div className="max-w-7xl grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-8 lg:gap-x-5 md:gap-y-10 lg:gap-y-12">
        {_.map(sortedTemplates, (template) => {
          const shouldOpen = initialTemplateId === template.id
          const isUnlisted = template.id === unlistedTemplate?.id
          return (
            <div className="flex-grow" key={`template-${template.name}-${template.id}`}>
              <TemplatePreviewContainer>
                {isPublic ? (
                  <TemplateModal
                    template={template}
                    sortBy={templateSortBy}
                    templatePreview={templatePreview(template)}
                    initiallyOpen={isUnlisted ? true : shouldOpen}
                    isUnlisted={isUnlisted}
                  />
                ) : (
                  <DuplicatePaywallAction
                    showPreview={true}
                    paywallId={template.id}
                    name={template.name}
                    cloneOptionType={CloneOptionType.AsTemplate}
                    renderButton={({ open }) => (
                      <TemplatePreviewContent
                        template={template}
                        templateSortBy={templateSortBy}
                        templatePreview={templatePreview(template)}
                        onOpen={() => {
                          if (shouldOpen) {
                            open()
                            router.replace(`/applications/${applicationId}/templates`, undefined, {
                              shallow: true,
                            })
                          }
                        }}
                        onClick={(e) => {
                          e.stopPropagation()
                          open()
                        }}
                      />
                    )}
                  />
                )}
              </TemplatePreviewContainer>
            </div>
          )
        })}
        {isFetchingNextTemplates && (
          <>
            <TemplateSkeleton />
            <TemplateSkeleton />
            <TemplateSkeleton />
            <TemplateSkeleton />
          </>
        )}
      </div>
    </Container>
  )
}

const TemplateModal = ({
  template,
  sortBy,
  templatePreview,
  initiallyOpen,
  isUnlisted = false,
}: {
  template: TTemplate
  sortBy: TTemplateSortBy
  templatePreview?: any
  initiallyOpen: boolean
  isUnlisted?: boolean
}) => {
  const router = useRouter()
  const [templateId, setTemplateId] = useQueryState("templateId", queryTypes.integer)

  const [paywall] = useQuery(
    getPaywallPublic,
    { id: template.id },
    {
      suspense: false,
      enabled: (initiallyOpen || templateId === template.id) && !isUnlisted,
    }
  )

  const handleOpen = useCallback(() => {
    if (isUnlisted) {
      return
    }
    setTemplateId(template.id, { scroll: false })
  }, [setTemplateId, template.id, isUnlisted])

  const handleClose = useCallback(() => {
    setTemplateId(null, { scroll: false })
  }, [setTemplateId])

  useEffect(() => {
    if (initiallyOpen) {
      handleOpen()
    }
  }, [initiallyOpen, handleOpen])

  return (
    <ModalOverlay
      includeX={true}
      size="sm"
      startOpen={initiallyOpen}
      renderButton={({ open }) => {
        if (isUnlisted) {
          return null
        }

        return (
          <TemplatePreviewContent
            template={template}
            templateSortBy={sortBy}
            templatePreview={templatePreview}
            onClick={(e) => {
              e.stopPropagation()
              handleOpen()
              open()
            }}
            isPublic={true}
          />
        )
      }}
      render={() => {
        return (
          <div className="flex flex-col items-center gap-5 ">
            <h2 className="opacity-70 text-center -mt-1">{template.name}</h2>

            <div className="flex flex-col w-full items-center justify-center gap-3">
              {(isUnlisted ? template : paywall) && (
                <PaywallPreview
                  previewUrl={(isUnlisted ? (template as any) : paywall)!.url}
                  subs={[]}
                  viewFreeTrial={false}
                  maxWidth={300}
                  deviceColor={"hsl(218,14%,30%)"}
                  showNotch={false}
                  backgroundColor={(isUnlisted ? (template as any) : paywall)!.backgroundColorHex}
                  pageStyles={(isUnlisted ? (template as any) : paywall)!.pageStyles}
                  variables={{}}
                  paywall={isUnlisted ? (template as any) : paywall}
                />
              )}
              <span className="text-white text-sm opacity-50 tracking-loose">
                Last updated {moment(template.updatedAt).fromNow()}
              </span>
            </div>

            <SButton
              variant="juicy"
              className="!py-3 !text-base w-full rounded-xl"
              onClick={async () => {
                if (isUnlisted && "templateId" in template) {
                  return router.push(`/templates/${template.templateId}/duplicate`)
                }
                router.push(
                  `/select-application?pathname=${encodeURIComponent(
                    `applications/:app/templates?templateId=${template.id}`
                  )}`
                )
              }}
            >
              Use Template →
            </SButton>
          </div>
        )
      }}
      onClose={handleClose}
    />
  )
}

const TemplatePreviewContent = ({
  template,
  templateSortBy,
  templatePreview,
  onOpen,
  onClick,
  isPublic = false,
}: {
  template: TTemplate
  templateSortBy: TTemplateSortBy
  templatePreview: any
  onOpen?: () => void
  onClick: (e: React.MouseEvent) => void
  isPublic?: boolean
}) => {
  useEffect(() => {
    onOpen && onOpen()
  }, [onOpen])

  return (
    <div
      className="cursor-pointer h-full flex flex-col w-full items-center justify-center"
      onClick={onClick}
    >
      <TemplateName name={template.name} />
      <div className="text-center">
        {templateSortBy === "createdAtDesc" || templateSortBy === "createdAtAsc" ? (
          <Sublabel>Created {moment(template.createdAt).format("MMM D, YYYY")}</Sublabel>
        ) : (
          <Sublabel>Updated {moment(template.updatedAt).fromNow()}</Sublabel>
        )}
      </div>
      {isPublic ? (
        <HoverSkew
          key={template.id}
          className="!rounded-2xl h-full w-full"
          clickable
          glowStength="extra-light"
        >
          <ImageContainer>
            <PreviewImage
              url={templatePreview?.url}
              placeholder={
                !templatePreview?.url
                  ? "Preview Pending"
                  : !templatePreview?.active
                  ? "Preview Updating ..."
                  : undefined
              }
            />
          </ImageContainer>
        </HoverSkew>
      ) : (
        <div className="hover:scale-102 transition-transform duration-200 ease-out w-full h-full">
          <ImageContainer>
            <PreviewImage
              url={templatePreview?.url}
              placeholder={
                !templatePreview?.url
                  ? "Preview Pending"
                  : !templatePreview?.active
                  ? "Preview Updating ..."
                  : undefined
              }
            />
          </ImageContainer>
        </div>
      )}
    </div>
  )
}

export default TemplateGallery
